import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const EmailVerification = () => {
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser, resendVerificationEmail } = useAuth();

  // Resending requires an active (even if unverified) Firebase session —
  // there's no way to email a verification link from just an email address.
  // If someone lands here with no session (bookmarked link, days later, etc.),
  // send them to log in first — that attempt re-establishes the session this
  // page needs, then they can come straight back here.
  const hasSession = !!currentUser;

  const handleResendEmail = async () => {
    try {
      setError('');
      setMessage('');
      setLoading(true);
      const success = await resendVerificationEmail();
      if (success) {
        setMessage("Verification email has been sent. Please check your inbox — and your spam or junk folder if it doesn't show up in a minute.");
      } else {
        setError('Failed to send verification email. Please try again.');
      }
    } catch (err) {
      setError('Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify your email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent you a verification email. Please check your inbox (and your spam or junk folder) and click the verification link before logging in.
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {message && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{message}</span>
          </div>
        )}

        {hasSession ? (
          <div className="space-y-4">
            <button
              onClick={handleResendEmail}
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
            >
              {loading ? 'Sending...' : 'Resend verification email'}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="font-medium text-brand-600 hover:text-brand-500"
              >
                Return to login
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded text-sm">
              To resend your verification email, please log in with your email and password first — that's how we confirm which account to send it to. The login page will show a "Resend verification email" link once you try.
            </div>
            <Link
              to="/login"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700"
            >
              Go to login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;
